import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { formatMoney } from '../utils/formatters';
import { getAdTitle } from '../utils/adI18n';
import { CategoryTabs } from '../components/CategoryTabs';
import HomeTicker from '../components/HomeTicker';
import HubsMiniRow from '../components/HubsMiniRow';
import ScrollHintRow from '../components/common/ScrollHintRow';
import SmartGreeting from '../components/SmartGreeting';
import ProfileTypePicker, { getProfileRoute } from '../components/ProfileTypePicker';
import NearMeStateCard from '../components/NearMeStateCard';
import { reverseGeocode as runReverseGeocode } from '../utils/reverseGeocode';
import { usePostingChooser } from '../contexts/PostingChooserContext';
import { toast } from 'sonner';
import { Heart, Package, MapPin, X, SlidersHorizontal, Bell } from 'lucide-react';
import PremiumSortSheet from '../components/PremiumSortSheet';
import UnifiedFilterSheet, { countActiveFilters } from '../components/UnifiedFilterSheet';
import '../components/UnifiedFilterSheet.css';
import useLocationFilter from '../hooks/useLocationFilter';
import PremiumSearchBar from '../components/PremiumSearchBar';
import { ProductCard, ProductGrid } from '../components/ProductGrid';
import SponsoredRow from '../components/SponsoredRow';
import BoostTimerBadge from '../components/BoostTimerBadge';
import { searchAds } from '../lib/searchAds';
import { trackSearchSubmit, trackFilterApply, trackFavoriteAdd, trackAdOpen } from '../lib/analytics';
import { guardImage, getCategoryPlaceholder } from '../lib/imageTrust';
import { getAdCoverImage } from '../lib/adImages';
import { toAbsoluteUrl } from '../lib/media';
import { getApiBase } from '../api/httpClient';
import { getLocalizedLocationLabel } from '../components/AdInfoCard';
import { formatLocationLabel } from '../data/locations';
import { saveRecentCategory, getRecentCategories, extractPreferredCategories } from '../utils/forYou';
import { createSavedSearch } from '../api/savedSearches';
import { trackEvent } from '../utils/analytics';
import { getHiddenListingIds } from '../utils/hiddenListings';
import { generateSafeForYouFeed } from '../utils/safeForYou';
import { generateTrendingFeed, generateNearMeFeed, generateTopSearchFeed } from '../utils/discoveryFeeds';
import { getVisibleLiveMarketplaceAds } from '../utils/canonicalAdFilter';
import RecentSearchChips from '../components/RecentSearchChips';
import RecentlyViewedCarousel from '../components/RecentlyViewedCarousel';
import MarketplacePulse from '../components/MarketplacePulse';
import '../components/MarketplacePulse.css';
import TrendingSearchesChips from '../components/TrendingSearchesChips';
import '../components/TrendingSearchesChips.css';
import { getRecentSearches, addRecentSearch, clearRecentSearches } from '../lib/recentSearches';
import { sortAdsByPrice, isPriceAsc, isPriceDesc } from '../utils/priceUtils';
import { consumeWalletReturnContext, savePageViewState, loadPageViewState } from '../lib/routeStateRestore';
import InlineError from '../components/InlineError';
import { dedupeAdsById } from '../utils/dedupeAds';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { resetAllListingFilters } from '../utils/filterReset';
import { resolveCountryLabel, resolveCityLabel } from '../utils/locationLabels';
import './Home.css';

const HOME_LEAN_FLAGS = {
  showRecentSearches: false,
  showTicker: false,
  showSmartGreeting: false,
};

// Check if low bandwidth mode is enabled
const isLowBandwidthMode = () => {
  return localStorage.getItem('lowBandwidthMode') === 'true';
};

const HOME_VIEW_STATE_KEY = 'home';

// Unified filter defaults — same shape as Shop page
const HOME_DEFAULT_FILTERS = {
  country: '',
  city: '',
  priceMin: '',
  priceMax: '',
  sort: 'newest',
  deliveryAvailable: false,
};

function Home() {
  const { t, i18n } = useTranslation();
  const { lang } = useLanguage();
  const { 
    user, 
    isReady, 
    isLoggedIn, 
    needsProfileType, 
    setProfileType: authSetProfileType, 
    dismissProfileTypePicker 
  } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  const navigate = useNavigate();
  const location = useLocation();
  // iter290 — Same posting chooser used by the orange + button so the
  // empty-state "Post an ad" CTA never opens a black /post-ad route.
  const { openChooser } = usePostingChooser();

  // Location data for localized filter chip labels
  const { countries: locationCountries } = useLocationFilter();

  const [searchParams, setSearchParams] = useSearchParams();
  
  // Profile Type Picker - ONLY show after login when needed
  const [showProfilePicker, setShowProfilePicker] = useState(false);
  const pickerShownRef = useRef(false); // Prevent showing multiple times per session
  const dismissedThisSessionRef = useRef(false); // Track session dismissal
  
  // Show profile picker ONLY when:
  // 1. Auth is ready
  // 2. User is logged in
  // 3. User needs profile type (from backend)
  // 4. Not already shown this session
  // 5. Not dismissed this session
  useEffect(() => {
    if (!isReady) return; // Wait for auth to be ready
    if (!isLoggedIn) return; // NEVER show to guests
    if (!needsProfileType) return; // User already has profile type
    if (pickerShownRef.current) return; // Already shown once
    if (dismissedThisSessionRef.current) return; // User dismissed this session
    
    // Check session dismissal from sessionStorage
    const sessionDismissed = sessionStorage.getItem('wassla_profile_picker_dismissed_session');
    if (sessionDismissed) {
      dismissedThisSessionRef.current = true;
      return;
    }
    
    // Show picker after a brief delay (don't block first paint)
    const timer = setTimeout(() => {
      if (!pickerShownRef.current && !dismissedThisSessionRef.current) {
        pickerShownRef.current = true;
        setShowProfilePicker(true);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [isReady, isLoggedIn, needsProfileType]);
  
  // Handle profile type selection with routing
  const handleProfileTypeSelect = useCallback(async (type) => {
    // Save to backend via AuthContext
    const success = await authSetProfileType(type);
    
    if (success) {
      // Also cache locally for immediate routing
      localStorage.setItem('wassla_profile_type', type);
      localStorage.setItem('wassla_profile_type_set_at', new Date().toISOString());
      localStorage.setItem('hasCompletedProfileSelection', 'true');
      
      // Get the route for this profile type
      const targetRoute = getProfileRoute(type, true);
      
      // Show success toast
      toast.success(
        t('home.yourPreferenceHasBeenSaved'),
        { duration: 3000 }
      );
      
      // Close picker
      setShowProfilePicker(false);
      
      // Route to appropriate destination based on profile type
      if (type !== 'general' && targetRoute !== '/') {
        setTimeout(() => {
          navigate(targetRoute, { replace: true });
        }, 300);
      }
    }
  }, [isRTL, navigate, authSetProfileType]);
  
  // Handle picker close (user clicked "Later")
  const handlePickerClose = useCallback(async () => {
    setShowProfilePicker(false);
    dismissedThisSessionRef.current = true;
    
    // Mark in sessionStorage
    sessionStorage.setItem('wassla_profile_picker_dismissed_session', 'true');
    
    // Tell backend user dismissed
    await dismissProfileTypePicker();
  }, [dismissProfileTypePicker]);
  
  // Initialize state from URL params (for ticker navigation)
  const urlCategory = searchParams.get('category');
  const urlSearch = searchParams.get('search');
  
  // State
  const [searchQuery, setSearchQuery] = useState(urlSearch || '');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(urlCategory || 'all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [fallbackAds, setFallbackAds] = useState([]);
  
  // Image search state - results stay on Home, don't navigate to Shop
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [isImageSearchActive, setIsImageSearchActive] = useState(false);
  
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const lowBandwidth = isLowBandwidthMode();
  const pageSize = lowBandwidth ? 20 : 40; // Unified with Shop: 40 ads per page
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [sortOpen, setSortOpen] = useState(false); // Premium sort sheet state
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [locationFilter, setLocationFilter] = useState('');
  const [heartBurstMap, setHeartBurstMap] = useState({});

  // Unified filters state — same shape as Shop page (single source of truth)
  const [homeFilters, setHomeFilters] = useState({
    country: '',
    city: '',
    priceMin: '',
    priceMax: '',
    sort: 'newest',
    deliveryAvailable: false,
  });

  // Chip label resolver — declared here (after homeFilters) so the closure
  // can safely read homeFilters.country to pick the correct city list.
  // See utils/locationLabels.js for the shared, synchronous fallback that
  // prevents raw slugs (e.g. "egypt") from leaking into the UI.
  const getLocationLabel = useCallback((type, key) => {
    if (type === 'country') return resolveCountryLabel(key, isArabic, locationCountries);
    const cityList = (locationCountries.find((c) => c.key === homeFilters.country)?.cities) || [];
    return resolveCityLabel(key, isArabic, cityList);
  }, [locationCountries, isArabic, homeFilters.country]);

  const [recentSearches, setRecentSearches] = useState([]);
  const [forYouActive, setForYouActive] = useState(false);
  const [discoveryMode, setDiscoveryMode] = useState('');
  const [isRestoringViewState, setIsRestoringViewState] = useState(true);
  // iter316/iter319 — User-granted location for Near Me ranking.
  // Stays in localStorage scoped per session; coordinates are NEVER
  // sent anywhere (only consumed locally to re-rank /api/ads). State
  // shape: { lat, lng, ts } | null.
  const [userLocation, setUserLocation] = useState(() => {
    try {
      const raw = localStorage.getItem('wassla:userLocation');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') return parsed;
    } catch (_) { /* malformed cache → ignore */ }
    return null;
  });
  // 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported' | 'error'
  const [locationStatus, setLocationStatus] = useState(() => (
    typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.geolocation
      ? 'idle' : 'unsupported'
  ));
  // iter319/iter322 — Manual location fallback. The premium multi-
  // country picker (`<LocationPickerSheet>`) lets the buyer choose
  // Country → Region → City → Area when GPS is denied / unreliable
  // (very common in Sudan). Persisted as a single object so revisits
  // restore the full selection. The `city` field still feeds into
  // `generateNearMeFeed` for the city-text branch — additional
  // fields are kept for Phase B (Shop filter + Post Ad re-use).
  const [manualLocation, setManualLocation] = useState(() => {
    try {
      const raw = localStorage.getItem('wassla:manualLocation');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (_) { /* malformed cache */ }
    return null;
  });
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  // iter319 — City derived from a successful GPS reverse-geocode.
  // Lets the city-text branch in `generateNearMeFeed` fire even when
  // no ads have lat/lng. Not persisted — re-derived each session
  // because the user might roam.
  const [gpsCity, setGpsCity] = useState('');
  const pendingScrollRestoreRef = useRef(null);
  const searchUserKey = user?.user_id || user?.id || 'guest';

  // iter316/iter319 — One-tap geolocation request. Privacy-first:
  //   • Never auto-requests — only fires on user button tap.
  //   • Coordinates are cached in localStorage ONLY when the user
  //     grants permission; nothing is sent to /api or to sellers.
  //   • A failure / denial falls back to manual city picker (never
  //     blocks UI).
  //   • Re-tapping the button asks again (the browser can re-prompt
  //     or honour a remembered choice — either is fine). iter319:
  //     `enableHighAccuracy=true` improves precision on mobile so
  //     the 10/25/50 km expanding rings make sense.
  const requestUserLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationStatus('unsupported');
      return;
    }
    setLocationStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          ts: Date.now(),
        };
        setUserLocation(next);
        setLocationStatus('granted');
        try { localStorage.setItem('wassla:userLocation', JSON.stringify(next)); } catch (_) { /* quota / private mode */ }
        // iter319 — Fire-and-forget reverse-geocode so the city-text
        // branch in `generateNearMeFeed` still fires when no ads
        // carry lat/lng (the marketplace's current reality). Errors
        // are silent; the city-text branch just stays empty.
        runReverseGeocode(next.lat, next.lng).then((geo) => {
          const cityGuess = geo?.city || geo?.locality || geo?.principalSubdivision || '';
          if (cityGuess) setGpsCity(cityGuess);
        }).catch(() => { /* offline / ad-blocker — fine */ });
      },
      (err) => {
        // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3
        setLocationStatus(err?.code === 1 ? 'denied' : 'error');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60 * 1000 }
    );
  }, []);

  // iter322 — Apply a buyer-confirmed location from the picker.
  // We persist the FULL object so Shop (Phase B) and Post Ad
  // (Phase B) can re-use the same selection. The legacy
  // `wassla:manualCity` key is kept readable for back-compat.
  const applyManualLocation = useCallback((loc) => {
    const safe = loc && typeof loc === 'object' ? loc : null;
    setManualLocation(safe);
    setCityPickerOpen(false);
    try {
      if (safe) {
        localStorage.setItem('wassla:manualLocation', JSON.stringify(safe));
        if (safe.city) localStorage.setItem('wassla:manualCity', safe.city);
      } else {
        localStorage.removeItem('wassla:manualLocation');
        localStorage.removeItem('wassla:manualCity');
      }
    } catch (_) { /* quota / private mode */ }
  }, []);

  const clearManualLocation = useCallback(() => {
    setManualLocation(null);
    try {
      localStorage.removeItem('wassla:manualLocation');
      localStorage.removeItem('wassla:manualCity');
    } catch (_) { /* quota / private mode */ }
  }, []);

  // Analytics tracking helper (fire and forget)
  const trackEvent = useCallback((type, extra = {}) => {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, ...extra })
      }).catch(() => {}); // Ignore errors
    } catch (e) {
      // Ignore
    }
  }, []);
  
  // Helper function to get full image URL — iter275: delegate to the
  // canonical resolver so production same-origin fallback applies here
  // too (this function is still referenced by a few legacy call sites).
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.svg';
    return toAbsoluteUrl(imagePath);
  };

  // Get a trusted image URL — seed/stock images are replaced with category placeholders
  // iter274 — use the canonical normaliser so every legacy field name
  // (`images`, `image_urls`, `photos`, `imageUrl`, `photo_url`, `media`,
  // `thumbnail`, etc.) is handled the same way Shop / MyAds / Ad Details
  // already do. The card is rendered with a real URL whenever the ad
  // has ANY valid persistent image; placeholder only when truly empty.
  const getTrustedImage = (product) => {
    const cover = getAdCoverImage(product);
    if (!cover) return getCategoryPlaceholder(product?.category_id || product?.category);
    return guardImage(cover, product?.category_id || product?.category);
  };

  // iter324 — Mark Near Me as the active discovery surface on body so
  // the floating <GuestSignInBanner> can suppress itself when its
  // sticky bottom position would cover the Near Me action buttons
  // (Use my location / Choose your city / Sign in). Cleared on unmount
  // and whenever the user switches away from Near Me.
  useEffect(() => {
    if (discoveryMode === 'near_me') {
      document.body.setAttribute('data-nearme-active', '1');
    } else {
      if (document.body.getAttribute('data-nearme-active') === '1') {
        document.body.removeAttribute('data-nearme-active');
      }
    }
    return () => {
      if (document.body.getAttribute('data-nearme-active') === '1') {
        document.body.removeAttribute('data-nearme-active');
      }
    };
  }, [discoveryMode]);

  // Handle image load error - use gray placeholder
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = '/placeholder.svg';
    // Remove shimmer effect on error
    e.target.parentElement?.classList.add('loaded');
  };

  // Handle image load success
  const handleImageLoad = (e) => {
    e.target.parentElement?.classList.add('loaded');
  };

  const handleFavoriteClick = async (adId, e) => {
    await toggleFavorite(adId, e);
    trackFavoriteAdd(adId, user?.user_id);

    setHeartBurstMap((prev) => ({ ...prev, [adId]: true }));
    setTimeout(() => {
      setHeartBurstMap((prev) => ({ ...prev, [adId]: false }));
    }, 220);

    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // iter280 — Probe the marketplace-wide public-stats endpoint once on
  // mount. We use it to decide whether the empty state should suggest
  // "Trending now" chips (which only make sense if there ARE active
  // ads to draw trends from) or hide them entirely. A truly empty
  // marketplace should not pretend it has trending searches.
  const [marketplaceHasLiveAds, setMarketplaceHasLiveAds] = useState(null);
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const r = await fetch(`${getApiBase()}/api/marketplace/public-stats`, { credentials: 'include' });
        if (!r.ok) throw new Error(`${r.status}`);
        const data = await r.json();
        if (!aborted) {
          setMarketplaceHasLiveAds(Boolean(data?.marketplace_has_live_ads));
        }
      } catch (_e) {
        if (!aborted) setMarketplaceHasLiveAds(true); // optimistic — don't suppress trends on probe failure
      }
    })();
    return () => { aborted = true; };
  }, []);

  useEffect(() => {
    setRecentSearches(getRecentSearches(searchUserKey));
  }, [searchUserKey]);

  useEffect(() => {
    const restoreContext = consumeWalletReturnContext(location.pathname);
    if (restoreContext) {
      const saved = loadPageViewState(HOME_VIEW_STATE_KEY);
      if (saved) {
        if (typeof saved.searchQuery === 'string') setSearchQuery(saved.searchQuery);
        if (typeof saved.selectedCategory === 'string') setSelectedCategory(saved.selectedCategory);
        if (typeof saved.sortBy === 'string') setSortBy(saved.sortBy);
        if (saved.priceRange && typeof saved.priceRange === 'object') {
          setPriceRange({
            min: saved.priceRange.min || '',
            max: saved.priceRange.max || '',
          });
        }
        if (typeof saved.locationFilter === 'string') setLocationFilter(saved.locationFilter);
      }

      if (typeof restoreContext.scrollTop === 'number') {
        pendingScrollRestoreRef.current = restoreContext.scrollTop;
      }
    }

    setIsRestoringViewState(false);
  }, [location.pathname]);

  useEffect(() => {
    savePageViewState(HOME_VIEW_STATE_KEY, {
      searchQuery,
      selectedCategory,
      sortBy,
      priceRange,
      locationFilter,
    });
  }, [searchQuery, selectedCategory, sortBy, priceRange, locationFilter]);

  // Sync state with URL params (for ticker navigation)
  useEffect(() => {
    const newCategory = searchParams.get('category');
    const newSearch = searchParams.get('search');
    
    // Only update if values actually changed to avoid infinite loops
    if (newCategory && newCategory !== selectedCategory) {
      setSelectedCategory(newCategory);
    } else if (!newCategory && selectedCategory !== 'all' && !urlCategory) {
      // If category removed from URL and wasn't set by user, reset to all
      // But only if it wasn't the initial URL param
    }
    
    if (newSearch !== null && newSearch !== searchQuery) {
      setSearchQuery(newSearch);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isRestoringViewState) return;
    setPage(1);
    fetchProducts(1, true);
    
    // Track category view for analytics
    if (selectedCategory && selectedCategory !== 'all') {
      trackEvent('VIEW_CATEGORY', { category_slug: selectedCategory });
    }
  }, [selectedCategory, searchQuery, trackEvent, isRestoringViewState]);

  // Refetch when unified filters change (country, city, price, sort, delivery from filter sheet)
  useEffect(() => {
    if (isRestoringViewState) return;
    fetchProducts(1, true);
  }, [homeFilters.country, homeFilters.city, homeFilters.priceMin, homeFilters.priceMax, homeFilters.sort, homeFilters.deliveryAvailable]);

  useEffect(() => {
    if (isRestoringViewState || loading) return;
    if (pendingScrollRestoreRef.current == null) return;

    const container = document.querySelector('.main-content');
    if (container) {
      container.scrollTop = pendingScrollRestoreRef.current;
    }
    pendingScrollRestoreRef.current = null;
  }, [loading, products.length, isRestoringViewState]);

  const fetchCategories = async () => {
    try {
      // Use full categories endpoint to match dropdown - all active categories
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        // Deduplicate by key and sort
        const uniqueMap = new Map();
        data.forEach(cat => {
          if (!uniqueMap.has(cat.key)) {
            uniqueMap.set(cat.key, cat);
          }
        });
        setCategories(Array.from(uniqueMap.values()));
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async (pageNum = 1, reset = false) => {
    if (pageNum === 1) setLoading(true);
    setFetchError(false);
    
    try {
      const data = await searchAds({
        pageNum,
        pageSize,
        selectedCategory,
        searchQuery,
        sortBy: homeFilters.sort || sortBy,
        priceRange: {
          min: homeFilters.priceMin || priceRange.min,
          max: homeFilters.priceMax || priceRange.max,
        },
        locationFilter,
        country: homeFilters.country || '',
        city: homeFilters.city || '',
        // iter322 Phase B — Strict manual filter at API level so the
        // Egypt filter never returns Sudanese ads (and vice versa).
        locationStrict: Boolean(homeFilters.country),
        // iter328 — Delivery availability filter.
        deliveryAvailable: Boolean(homeFilters.deliveryAvailable),
        strict: true,
      });

      const newProducts = data.ads || [];
      
      // Store search suggestions and fallback ads
      setSearchSuggestions(data.suggestions || []);
      // iter333 — Backend always returns [] for fallback_ads (no
      // mock-when-empty per P0 spec). Defensive parity filter still
      // applied in case a stale CDN response slips through.
      setFallbackAds(getVisibleLiveMarketplaceAds(data.fallback_ads || []));
        
        // CRITICAL: Deduplicate by ad_id to prevent same ad appearing twice
        if (reset) {
          // iter320 — Defense-in-depth canonical filter. Backend
          // already enforces `_build_public_live_ads_query`, but if
          // a non-active row ever sneaks in via a hub/search/merge
          // path the shared helper drops it before render so Home
          // never disagrees with the Admin "Live Shop Ads" count.
          setProducts(getVisibleLiveMarketplaceAds(newProducts));
        } else {
          // For pagination, merge with existing and re-apply the
          // canonical filter so the combined list is always strictly
          // the public-visible set (dedup happens inside the helper).
          setProducts(prev => getVisibleLiveMarketplaceAds([...prev, ...newProducts]));
        }
        
        setHasMore(newProducts.length === pageSize);
        setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      if (products.length === 0) setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  // Unified filter handlers for UnifiedFilterSheet (same pattern as Shop)
  const handleFiltersChange = useCallback((newFilters) => {
    setHomeFilters(newFilters);
    // Sync to individual state for legacy code paths
    setPriceRange({ min: newFilters.priceMin || '', max: newFilters.priceMax || '' });
    setSortBy(newFilters.sort || 'newest');
  }, []);

  // Apply filters and refresh products
  const applyFilters = useCallback(() => {
    setPage(1);
    setShowFilters(false);
    trackFilterApply({ ...homeFilters, category: selectedCategory, source: 'home' });
  }, [homeFilters, selectedCategory]);

  // Clear all filters — ROOT-CAUSE fix: uses ONE shared reset util with Shop.
  // Wipes unified filter object, legacy state, discovery/image-search state,
  // category/subcategory, search query, URL params, sessionStorage cache,
  // paging, and closes the sheet. Single source of truth.
  const clearFilters = useCallback(() => {
    resetAllListingFilters({
      setters: {
        setFilters: setHomeFilters,
        setPriceRange,
        setLocationFilter,
        setSortBy,
        setSelectedCategory,
        setSearchQuery,
        setDiscoveryMode,
        setForYouActive,
        setImageSearchResults,
        setIsImageSearchActive,
        setPage,
        setShowFilters,
      },
      pageKey: HOME_VIEW_STATE_KEY,
      searchParams,
      setSearchParams,
      defaults: HOME_DEFAULT_FILTERS,
    });
  }, [searchParams, setSearchParams]);

  // Save current search (Saved Search Alerts, P2). Persists the active
  // filter set to `db.saved_searches` so the 15-min sweep can notify the
  // user when new matching ads are posted. Authenticated only.
  const [savingSearch, setSavingSearch] = useState(false);
  const handleSaveSearch = useCallback(async () => {
    if (!user?.user_id) {
      toast.error(t('savedSearches.authRequired'));
      return;
    }
    const filters = {
      query: (searchQuery || '').trim(),
      category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : '',
      location: (locationFilter || '').trim(),
    };
    const minP = parseFloat(priceRange.min ?? homeFilters.priceMin ?? '');
    const maxP = parseFloat(priceRange.max ?? homeFilters.priceMax ?? '');
    if (!Number.isNaN(minP)) filters.min_price = minP;
    if (!Number.isNaN(maxP)) filters.max_price = maxP;
    const hasAny =
      filters.query || filters.category || filters.location ||
      filters.min_price != null || filters.max_price != null;
    if (!hasAny) {
      toast.error(t('savedSearches.needFilters'));
      return;
    }
    const label =
      filters.query ||
      filters.category ||
      filters.location ||
      t('savedSearches.defaultLabel');
    setSavingSearch(true);
    // Lightweight MVP log — fire on tap (intent). Runs before the API call
    // so failures (e.g., 25-limit reached) are still captured.
    trackEvent('save_search_clicked', { filters }, user?.user_id);
    try {
      const res = await createSavedSearch({ filters, label });
      if (res?.deduped) {
        toast.success(t('savedSearches.alreadySaved'));
      } else {
        toast.success(t('savedSearches.saved'));
      }
    } catch (e) {
      const msg = e?.message?.includes('limit reached')
        ? t('savedSearches.limitReached')
        : t('savedSearches.saveFailed');
      toast.error(msg);
    } finally {
      setSavingSearch(false);
    }
  }, [user?.user_id, searchQuery, selectedCategory, locationFilter, priceRange, homeFilters, t]);

  // Check if any filters are active (includes unified filter state, quick-chip
  // discovery mode, image search, search query, and category drill-down).
  // This drives whether the Clear-All chip bar is rendered, giving users a
  // single visible entry point to reset any active filter state.
  const hasActiveFilters = Boolean(
    sortBy !== 'newest' ||
    priceRange.min ||
    priceRange.max ||
    locationFilter ||
    homeFilters.country ||
    homeFilters.city ||
    homeFilters.priceMin ||
    homeFilters.priceMax ||
    (homeFilters.sort && homeFilters.sort !== 'newest') ||
    discoveryMode ||
    forYouActive ||
    isImageSearchActive ||
    searchQuery ||
    (selectedCategory && selectedCategory !== 'all')
  );

  const handleCategoryChange = (categoryKey) => {
    setDiscoveryMode('');
    setForYouActive(false);
    setSelectedCategory(categoryKey);
    saveRecentCategory(categoryKey, searchUserKey);
  };

  // Search handler - used by PremiumSearchBar
  const handleSearchSubmit = (term) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    setSearchQuery(trimmed);
    addRecentSearch(trimmed, searchUserKey);
    setRecentSearches(getRecentSearches(searchUserKey));
    trackSearchSubmit(trimmed, { source: 'home', category: selectedCategory });
  };

  const quickChips = useMemo(() => {
    return [
      {
        key: 'for_you',
        label: t('home.forYou'),
        onClick: () => {
          setDiscoveryMode('for_you');
          setForYouActive(true);
          setSelectedCategory('all');
          setSearchQuery('');
          setLocationFilter('');
        },
      },
      {
        key: 'trending',
        label: t('home.trending'),
        onClick: () => {
          setDiscoveryMode('trending');
          setForYouActive(false);
          setSelectedCategory('all');
          setSearchQuery('');
          setLocationFilter('');
        },
      },
      {
        key: 'near_me',
        label: t('home.nearMe'),
        onClick: () => {
          setDiscoveryMode('near_me');
          setForYouActive(false);
          setSelectedCategory('all');
          setSearchQuery('');
          setLocationFilter('');
        },
      },
      {
        key: 'top_search',
        label: t('home.topSearch'),
        onClick: () => {
          setDiscoveryMode('top_search');
          setForYouActive(false);
          setSelectedCategory('all');
          setSearchQuery('');
          setLocationFilter('');
        },
      },
    ];
  }, [isRTL]);

  const handleRecentSearchSelect = (term) => {
    const trimmed = String(term || '').trim();
    if (!trimmed) return;
    setSearchQuery(trimmed);
    trackEvent('SEARCH', { query: trimmed });
  };

  const handleClearRecentSearches = () => {
    clearRecentSearches(searchUserKey);
    setRecentSearches([]);
  };

  // For You — safe personalized feed with quality floor + diversity
  const forYouFeed = useMemo(() => {
    if (discoveryMode !== 'for_you' || products.length === 0) return [];
    const signals = {
      preferredCategories: extractPreferredCategories(getRecentCategories(searchUserKey), []),
      recentSearches: recentSearches || [],
      city: user?.city || user?.location || '',
    };
    return generateSafeForYouFeed(products, signals);
  }, [discoveryMode, products, recentSearches, user]);

  // Trending feed — sorted by popularity. Empty score → falls back
  // to recency tie-break in the generator, so the array always
  // mirrors `products` when no popularity signals exist yet.
  const trendingFeed = useMemo(() => {
    if (discoveryMode !== 'trending' || products.length === 0) return products;
    return generateTrendingFeed(products);
  }, [discoveryMode, products]);

  // Near Me feed — distance/city based, returns location metadata so
  // the UI can show the correct fallback hint when location is
  // unavailable.
  //
  // iter322 Phase A refinement — `locationFilterMode` controls fallback
  // behaviour:
  //   • 'manual-strict' — buyer picked a country/region/city in the
  //     LocationPickerSheet. The pool is hard-filtered to ads that
  //     match the selection. NO fallback ads from other countries.
  //   • 'gps-nearby'    — buyer used the GPS "Use my location" CTA.
  //     Falls back to live ads when no GPS match found (legacy UX).
  //   • 'none'          — no location signal active. Same as before.
  const locationFilterMode = useMemo(() => {
    if (manualLocation && (manualLocation.country || manualLocation.city || manualLocation.area)) {
      return 'manual-strict';
    }
    if (userLocation || gpsCity) {
      return 'gps-nearby';
    }
    return 'none';
  }, [manualLocation, userLocation, gpsCity]);

  const nearMeResult = useMemo(() => {
    if (discoveryMode !== 'near_me' || products.length === 0) {
      return {
        items: products, hasLocation: false, hasNearbyMatch: false,
        matchRadiusKm: null, nearbyCount: 0, totalLive: products.length, adsWithCoords: 0,
        locationFilterMode, strictManualEmpty: false,
      };
    }
    // iter322 — userCity precedence: manual picker (city OR area)
    // → GPS reverse-geocoded city → profile city.
    const userCity = (manualLocation && (manualLocation.city || manualLocation.area))
      || gpsCity
      || user?.city
      || user?.location
      || '';
    // Prefer the freshly user-granted GPS coordinates over any
    // profile values, since they reflect "right now" intent.
    const userLat = userLocation?.lat ?? user?.latitude;
    const userLng = userLocation?.lng ?? user?.longitude;
    const result = generateNearMeFeed(products, userLat, userLng, userCity, {
      locationFilterMode,
      manualLocation,
    });
    // iter319 — Dev-only diagnostics. Disabled in prod builds via
    // NODE_ENV check so production users see a clean console.
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
      console.debug('[NearMe]', {
        userLat, userLng, userCity,
        locationFilterMode,
        totalLive: result.totalLive,
        adsWithCoords: result.adsWithCoords,
        nearbyCount: result.nearbyCount,
        matchRadiusKm: result.matchRadiusKm,
        hasLocation: result.hasLocation,
        hasNearbyMatch: result.hasNearbyMatch,
        strictManualEmpty: result.strictManualEmpty,
      });
    }
    return result;
  }, [discoveryMode, products, user, userLocation, manualLocation, gpsCity, locationFilterMode]);
  const nearMeFeed = nearMeResult.items;

  // Top Search feed — sorted by keyword relevance with recency tie-break.
  const topSearchFeed = useMemo(() => {
    if (discoveryMode !== 'top_search' || products.length === 0) return products;
    return generateTopSearchFeed(products);
  }, [discoveryMode, products]);

  // Active feed based on discovery mode.
  //
  // iter315 — Every discovery mode now falls back to `products`
  // (= raw /api/ads response = canonical live ads) when its scored
  // feed yields zero matches. This was the root of the "No ads are
  // live yet" bug — Trending / Top Search / Near Me would silently
  // collapse to an empty array (e.g. when no analytics existed yet)
  // and Home.js's empty-state logic would then mis-classify the
  // marketplace as dead.
  const activeFeed = useMemo(() => {
    let feed;
    if (isImageSearchActive) feed = imageSearchResults || [];
    else if (!discoveryMode) feed = products;
    else {
      switch (discoveryMode) {
        case 'for_you':    feed = forYouFeed.length    > 0 ? forYouFeed    : products; break;
        case 'trending':   feed = trendingFeed.length  > 0 ? trendingFeed  : products; break;
        // iter322 Phase A refinement — STRICT manual-location mode must
        // NOT fall back to `products`. If the buyer picked Egypt and we
        // have zero Egypt ads, the page should show the strict empty
        // state, NOT Sudan ads. GPS / no-location flows keep legacy
        // behaviour so users still see SOMETHING when the marketplace
        // has not localised most ads yet.
        case 'near_me':    feed = (locationFilterMode === 'manual-strict')
          ? nearMeFeed
          : (nearMeFeed.length > 0 ? nearMeFeed : products); break;
        case 'top_search': feed = topSearchFeed.length > 0 ? topSearchFeed : products; break;
        default:           feed = products;
      }
    }
    // Apply hidden listings filter — same as Shop's sortedAds pipeline
    const hiddenIds = getHiddenListingIds(searchUserKey);
    if (hiddenIds.length > 0) {
      feed = feed.filter(a => !hiddenIds.includes(a.ad_id));
    }
    return feed;
  }, [discoveryMode, forYouFeed, trendingFeed, nearMeFeed, topSearchFeed, products, isImageSearchActive, imageSearchResults, searchUserKey, locationFilterMode]);

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — single source of truth
  // normalize → filter → dedupe (shared with Shop, LadiesHub, etc.)
  // ═══════════════════════════════════════════════════════════
  const displayFeedRaw = useUnifiedSearch(activeFeed, searchQuery);

  // iter322 Phase A refinement — When the buyer applied a manual
  // location filter (Egypt / Cairo / Nasr City etc.) every surviving
  // ad MUST display a clean, country-anchored label so the UI never
  // surfaces ambiguous free-form text like "سوق كاستم" under an
  // Egypt filter. Strategy:
  //   • If the ad carries structured country/region/city/area fields,
  //     compose them through `formatLocationLabel` (handles AR/EN).
  //   • Otherwise borrow the buyer's selected country (since the ad
  //     just passed the strict country guard in matchesManualLocation)
  //     and merge with the ad's city/area for tier-aware display.
  // For GPS / no-location flows we keep the legacy localized label so
  // we don't regress the Sudan-only marketplace UX.
  const cleanLocationLabel = useCallback((ad) => {
    const lang = isArabic ? 'ar' : 'en';
    if (locationFilterMode === 'manual-strict' && manualLocation) {
      const adLoc = {
        country: ad?.country || ad?.countryCode || ad?.country_code || manualLocation.country,
        region: ad?.region || (ad?.city ? '' : manualLocation.region) || '',
        city: ad?.city || '',
        area: ad?.area || '',
      };
      if (adLoc.country || adLoc.city || adLoc.region || adLoc.area) {
        const composed = formatLocationLabel(adLoc, lang);
        if (composed) return composed;
      }
      // Fallback: use the filter label itself so the card never
      // surfaces stray free-form text under a strict filter.
      return formatLocationLabel(manualLocation, lang);
    }
    return getLocalizedLocationLabel(ad, lang);
  }, [locationFilterMode, manualLocation, isArabic]);

  // Single source of truth for ordering:
  //   - Discovery modes (for_you / trending / near_me / top_search) are
  //     CLIENT-generated and NOT price-sorted → client must sort.
  //   - Regular feed comes straight from /api/ads which already sorts
  //     purely by price (no tier interleaving) → trust the backend; do
  //     NOT re-sort client-side or we double-sort.
  const displayFeed = useMemo(() => {
    const isPriceSort = isPriceAsc(sortBy) || isPriceDesc(sortBy);
    if (!isPriceSort) return displayFeedRaw;
    if (!discoveryMode) return displayFeedRaw;        // backend is canonical
    return sortAdsByPrice(displayFeedRaw, isPriceAsc(sortBy) ? "asc" : "desc");
  }, [displayFeedRaw, sortBy, discoveryMode]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchProducts(page + 1, false);
    }
  };

  return (
    <div className="home-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Profile Type Picker - Shows ONLY after login when needed */}
      <ProfileTypePicker
        isOpen={showProfilePicker}
        onClose={handlePickerClose}
        onSelect={handleProfileTypeSelect}
      />
      
      {/* ========================================
          PREMIUM SEARCH BAR - AliExpress Style
      ======================================== */}
      <section className="wassla-search-section">
        <div className="wassla-search-container">
          <button
            className={`home-filter-btn ${countActiveFilters(homeFilters, HOME_DEFAULT_FILTERS) > 0 ? 'home-filter-btn-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            data-testid="home-filter-toggle"
          >
            <SlidersHorizontal size={18} />
            {countActiveFilters(homeFilters, HOME_DEFAULT_FILTERS) > 0 && (
              <span className="home-filter-badge">{countActiveFilters(homeFilters, HOME_DEFAULT_FILTERS)}</span>
            )}
          </button>
          <PremiumSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={(q) => {
              trackEvent('SEARCH', { query: q });
              handleSearchSubmit(q);
            }}
            showFilters={false}
            showCamera={false}
            placeholder={t('home.searchProductsServicesJobs')}
            dir={isRTL ? 'rtl' : 'ltr'}
            userId={searchUserKey}
          />
        </div>
      </section>

      <section className="home-quick-chips" data-testid="home-quick-chips">
        <ScrollHintRow
          storageKey="wassla-nudge-home-quick-chips"
          testid="home-quick-chips-hint"
        >
          <div className="home-quick-chips-row">
            {quickChips.map((chip) => {
              const isActive = chip.key === discoveryMode;
              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`home-quick-chip ${isActive ? 'home-quick-chip-active' : ''}`}
                  onClick={chip.onClick}
                  data-testid={`home-quick-chip-${chip.key}`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </ScrollHintRow>
      </section>

      {HOME_LEAN_FLAGS.showRecentSearches && (
        <RecentSearchChips
          items={recentSearches}
          onSelect={handleRecentSearchSelect}
          onClear={handleClearRecentSearches}
          isRTL={isRTL}
          title={t('home.recentSearches')}
        />
      )}

      {/* Marketplace Pulse — subtle "alive feeling" line. Self-hides if
          every counter is 0 / null. Reads /api/marketplace/pulse. */}
      <MarketplacePulse />

      {/* ========================================
          TRACKER STRIP - Below Search
      ======================================== */}
      {HOME_LEAN_FLAGS.showTicker && (
        <section className="wassla-tracker-section">
          <HomeTicker />
        </section>
      )}

      {/* ========================================
          SMART GREETING - Contextual Message
      ======================================== */}
      {HOME_LEAN_FLAGS.showSmartGreeting && <SmartGreeting />}

      {/* HORIZONTAL CATEGORY TABS - Main Navigation (moved above hubs) */}
      <CategoryTabs
        categories={categories}
        activeId={selectedCategory}
        onChange={handleCategoryChange}
        showRecommended={true}
      />

      {/* ========================================
          PREMIUM HUBS ROW - Compact Hub Cards
      ======================================== */}
      <section className="wassla-hubs-section">
        <HubsMiniRow />
      </section>

      {/* iter378 (v14) — Direct sponsored campaigns (homepage placement).
          Renders nothing when there are no active campaigns. */}
      {!discoveryMode && !searchQuery && !hasActiveFilters && !isImageSearchActive && (
        <SponsoredRow isArabic={isArabic} />
      )}

      {/* ========================================
          JUST POSTED - Newest Listings
          Hidden when: search active, filters active, image search active, or discovery mode active
      ======================================== */}
      {products.length > 0 && !discoveryMode && !searchQuery && !hasActiveFilters && !isImageSearchActive && (
        <section className="just-posted-section" data-testid="just-posted-section">
          <div className="just-posted-header">
            <div>
              <h2 className="just-posted-title">{t('home.justPosted')}</h2>
              <p className="just-posted-subtitle">{t('home.newListings')}</p>
            </div>
          </div>
          <ProductGrid dir={isArabic ? 'rtl' : 'ltr'}>
            {dedupeAdsById([...products]
              .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)))
              .slice(0, 6)
              .map((product) => (
                <ProductCard
                  key={`jp-${product.ad_id}`}
                  product={product}
                  imageUrl={getTrustedImage(product)}
                  title={getAdTitle(product, lang).text || product.title}
                  price={formatMoney(product.price, { locale: isArabic ? 'ar-SD' : 'en', currency: product.currency || 'SDG' })}
                  location={getLocalizedLocationLabel(product, isArabic ? 'ar' : 'en')}
                  isFavorite={isFavorite(product.ad_id)}
                  onOpen={() => navigate(`/ad/${product.ad_id}`)}
                  onToggleFavorite={handleFavoriteClick}
                  onImageError={handleImageError}
                  onImageLoad={handleImageLoad}
                  testId={`just-posted-${product.ad_id}`}
                >
                  <BoostTimerBadge
                    isBoosted={product.is_boosted}
                    boostedUntil={product.boosted_until}
                    status={product.status}
                    visibility={product.visibility}
                    availability={product.availability_status}
                    lang={lang}
                  />
                </ProductCard>
              ))}
          </ProductGrid>
        </section>
      )}

      {/* UNIFIED FILTER PANEL — same component as Shop page */}
      <UnifiedFilterSheet
        open={showFilters}
        onOpenChange={setShowFilters}
        filters={homeFilters}
        onFiltersChange={handleFiltersChange}
        onApply={applyFilters}
        onClear={clearFilters}
        title={t('home.filters')}
        defaultFilters={HOME_DEFAULT_FILTERS}
      >
        {({ draft, setDraft }) => (
          <>
            {/* Sort By */}
            <div className="ufs-section">
              <label className="ufs-section-label">{t('pss.sortBy')}</label>
              <select
                value={draft.sort || 'newest'}
                onChange={(e) => setDraft('sort', e.target.value)}
                className="ufs-select"
                data-testid="home-sort-select"
              >
                <option value="newest">{t('shopPage.sortNewest')}</option>
                <option value="price_low">{t('shopPage.sortPriceLow')}</option>
                <option value="price_high">{t('shopPage.sortPriceHigh')}</option>
                <option value="views">{t('shopPage.sortPopular')}</option>
              </select>
            </div>
            
            {/* Price Range */}
            <div className="ufs-section">
              <label className="ufs-section-label">{t('home.priceRange')}</label>
              <div className="ufs-price-row">
                <input
                  type="number"
                  placeholder={t('home.min')}
                  value={draft.priceMin || ''}
                  onChange={(e) => setDraft('priceMin', e.target.value)}
                  className="ufs-price-input"
                  data-testid="home-price-min"
                />
                <span className="ufs-price-sep">—</span>
                <input
                  type="number"
                  placeholder={t('home.max')}
                  value={draft.priceMax || ''}
                  onChange={(e) => setDraft('priceMax', e.target.value)}
                  className="ufs-price-input"
                  data-testid="home-price-max"
                />
              </div>
            </div>

            {/* iter328 — Delivery availability toggle. */}
            <div className="ufs-section">
              <label className="ufs-section-label">
                {isArabic ? 'التوصيل' : 'Delivery'}
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(draft.deliveryAvailable)}
                className={`ufs-delivery-toggle ${draft.deliveryAvailable ? 'is-on' : ''}`}
                onClick={() => setDraft('deliveryAvailable', !draft.deliveryAvailable)}
                data-testid="home-filter-delivery-available-toggle"
              >
                <span className="ufs-delivery-toggle-text">
                  <span className="ufs-delivery-toggle-icon" aria-hidden="true">🚚</span>
                  {isArabic ? 'التوصيل متاح' : 'Delivery available'}
                </span>
                <span className="ufs-delivery-toggle-knob" />
              </button>
            </div>
          </>
        )}
      </UnifiedFilterSheet>

      {/* ACTIVE FILTER CHIPS - Show when filters are applied (matching Shop layout) */}
      {hasActiveFilters && !showFilters && (
        <div className="active-filters-bar" data-testid="active-filters-bar">
          {homeFilters.country && (
            <span className="filter-chip filter-chip-green" data-testid="filter-chip-country">
              <MapPin size={14} />
              <span>{getLocationLabel('country', homeFilters.country)}</span>
              <button 
                className="chip-remove" 
                onClick={() => {
                  setHomeFilters(prev => ({ ...prev, country: '', city: '' }));
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          {homeFilters.city && (
            <span className="filter-chip filter-chip-green" data-testid="filter-chip-city">
              <span>{getLocationLabel('city', homeFilters.city)}</span>
              <button 
                className="chip-remove" 
                onClick={() => {
                  setHomeFilters(prev => ({ ...prev, city: '' }));
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          {(priceRange.min || priceRange.max) && (
            <span className="filter-chip" data-testid="filter-chip-price">
              <span>
                {t('adshar.price')} {priceRange.min || '0'} - {priceRange.max || '∞'}
              </span>
              <button 
                className="chip-remove" 
                onClick={() => {
                  setPriceRange({ min: '', max: '' });
                  setHomeFilters(prev => ({ ...prev, priceMin: '', priceMax: '' }));
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          {sortBy !== 'newest' && (
            <span className="filter-chip" data-testid="filter-chip-sort">
              <span>
                {sortBy === 'price_low' ? (t('shopPage.sortPriceLow')) :
                 sortBy === 'price_high' ? (t('shopPage.sortPriceHigh')) :
                 sortBy === 'views' ? (t('shopPage.sortPopular')) : sortBy}
              </span>
              <button 
                className="chip-remove" 
                onClick={() => {
                  setSortBy('newest');
                  setHomeFilters(prev => ({ ...prev, sort: 'newest' }));
                }}
              >
                <X size={12} />
              </button>
            </span>
          )}
          <button 
            className="clear-all-chips" 
            onClick={clearFilters}
            data-testid="clear-all-filters"
          >
            {t('home.clearAll')}
          </button>
          {user?.user_id && (
            <button
              className="save-search-chip"
              onClick={handleSaveSearch}
              disabled={savingSearch}
              data-testid="save-search-btn"
              aria-label={t('savedSearches.save')}
            >
              <Bell size={12} />
              <span>{t('savedSearches.save')}</span>
            </button>
          )}
        </div>
      )}

      {/* PRODUCT FEED - Main Content Area */}
      <div className="products-feed">
        {/* For You section heading */}
        {discoveryMode === 'for_you' && !isImageSearchActive && (
          <div className="for-you-heading" data-testid="for-you-heading">
            <h2 className="for-you-title">{t('home.forYou')}</h2>
            <p className="for-you-subtitle">{t('home.basedOnYourActivity')}</p>
          </div>
        )}
        {discoveryMode === 'trending' && !isImageSearchActive && (
          <div className="for-you-heading">
            <h2 className="for-you-title">{t('home.trending')}</h2>
            <p className="for-you-subtitle">{t('home.mostPopularRightNow')}</p>
          </div>
        )}
        {discoveryMode === 'near_me' && !isImageSearchActive && (
          <div className="for-you-heading">
            <h2 className="for-you-title">{t('home.nearYou')}</h2>
            <p className="for-you-subtitle">{t('home.listingsNearby')}</p>
            {/* iter319 — Premium Near Me state card. Five message
                states (A→E) collapse into a single bilingual card
                so the UI never looks like an error box and the
                copy always tells the user what to do next. */}
            <NearMeStateCard
              t={t}
              isRTL={isRTL}
              isGuest={!user}
              locationStatus={locationStatus}
              nearMeResult={nearMeResult}
              location={manualLocation}
              pickerOpen={cityPickerOpen}
              onRequestLocation={requestUserLocation}
              onOpenPicker={() => setCityPickerOpen(true)}
              onClosePicker={() => setCityPickerOpen(false)}
              onApplyLocation={applyManualLocation}
              onClearLocation={clearManualLocation}
              onSignIn={() => navigate('/auth?mode=login')}
            />
          </div>
        )}
        {discoveryMode === 'top_search' && !isImageSearchActive && (
          <div className="for-you-heading">
            <h2 className="for-you-title">{t('home.topSearched')}</h2>
            <p className="for-you-subtitle">{t('home.whatOthersAreSearching')}</p>
          </div>
        )}

        {/* Image Search Active indicator + clear */}
        {isImageSearchActive && (
          <div className="image-search-header" data-testid="image-search-controls">
            <div className="image-search-badge">
              <span>{t('home.imageSearchActive')}</span>
              <span className="image-search-count">
                {isRTL ? `${imageSearchResults?.length || 0} نتيجة مشابهة` : `${imageSearchResults?.length || 0} similar items`}
              </span>
            </div>
            <button 
              className="btn-clear-image-search"
              onClick={() => {
                setImageSearchResults([]);
                setIsImageSearchActive(false);
              }}
              data-testid="clear-image-search-btn"
            >
              {t('home.clearImageSearch')}
            </button>
          </div>
        )}

        {/* Products Grid - Show image search results OR regular products */}
        {loading && page === 1 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('home.loadingProducts')}</p>
          </div>
        ) : fetchError && (displayFeed || []).length === 0 ? (
          <InlineError onRetry={() => fetchProducts(1, true)} />
        ) : (displayFeed || []).length === 0 ? (
          (() => {
            // iter322 Phase A refinement — When the buyer is in Near Me
            // with a MANUAL location and zero ads match, the
            // NearMeStateCard above already renders the premium empty
            // state with "Change location" + "Clear location". Showing
            // a second generic "No matching ads found" empty state
            // below would imply the SEARCH BAR is the problem — it's
            // not, the LOCATION FILTER is. So we suppress the generic
            // empty state and let the NearMeStateCard speak.
            if (discoveryMode === 'near_me' && nearMeResult?.strictManualEmpty) {
              return null;
            }
            // iter315 — Empty state with 3 modes. Suppress trending /
            // suggestions / fallback when the marketplace truly has 0
            // active ads, because suggesting "trending searches" on a
            // dead marketplace is misleading.
            //
            // Mode selection (in priority order):
            //   1. marketplace genuinely empty → 'marketplace_empty'.
            //   2. user typed a search query    → 'search'.
            //   3. user picked a category       → 'category'.
            //   4. otherwise (live ads exist
            //      but a discovery filter
            //      ate the page)               → 'search' fall-back
            //      so we never falsely tell a
            //      live marketplace it's dead.
            const hasSearchQuery = (searchQuery || '').trim().length > 0;
            const hasCategoryFilter = selectedCategory && selectedCategory !== 'all';
            const marketplaceIsDead = marketplaceHasLiveAds === false;
            const hasDeliveryFilter = Boolean(homeFilters.deliveryAvailable);

            let mode = 'category';
            if (marketplaceIsDead) mode = 'marketplace_empty';
            else if (hasDeliveryFilter) mode = 'delivery';
            else if (hasSearchQuery) mode = 'search';
            else if (!hasCategoryFilter) mode = 'search';

            const COPY = {
              marketplace_empty: {
                icon: '🛍️',
                title: isArabic ? 'لا توجد إعلانات منشورة حالياً' : 'No ads are live yet',
                text: isArabic ? 'ستظهر الإعلانات الجديدة هنا قريباً. كن أول من ينشر إعلاناً.' : 'New listings will appear here soon. Be the first to post an ad.',
                primaryLabel: isArabic ? 'انشر إعلاناً' : 'Post an ad',
                // iter290 — Open the SAME posting chooser sheet the
                // orange + button opens. The legacy `/post-ad` route
                // never existed (real routes are /add-ad and /create-ad)
                // so navigating there showed a black page.
                primaryAction: () => openChooser(null),
                showTrending: false,
                showFallback: false,
              },
              // Delivery filter active with zero matches — NEVER fall back to
              // the unfiltered feed; offer a clear-filters escape hatch.
              delivery: {
                icon: '🚚',
                title: isArabic ? 'لا توجد إعلانات بتوصيل متاح' : 'No delivery listings found',
                text: isArabic ? 'جرّب تغيير الفلاتر أو مسحها لعرض المزيد من الإعلانات.' : 'Try changing or clearing your filters.',
                primaryLabel: isArabic ? 'مسح الفلاتر' : 'Clear filters',
                primaryAction: () => clearFilters(),
                showTrending: false,
                showFallback: false,
              },
              search: {
                icon: '🔍',
                title: isArabic ? 'لم يتم العثور على إعلانات مطابقة' : 'No matching ads found',
                text: isArabic ? 'جرّب كلمة بحث أخرى أو تصفح الأقسام الموجودة.' : 'Try a different keyword or browse the categories below.',
                primaryLabel: isArabic ? 'مسح البحث' : 'Clear search',
                primaryAction: () => setSearchQuery(''),
                showTrending: marketplaceHasLiveAds !== false,
                showFallback: marketplaceHasLiveAds !== false && fallbackAds.length > 0,
              },
              category: {
                icon: '🛍️',
                title: isArabic ? 'لا توجد إعلانات في هذا القسم بعد' : 'No ads yet in this section',
                text: isArabic ? 'ستظهر الإعلانات الجديدة هنا قريباً. يمكنك تصفح أقسام أخرى أو كن أول من ينشر إعلاناً.' : 'New listings will appear here soon. You can explore other categories or be the first to post an ad.',
                primaryLabel: isArabic ? 'تصفح الأقسام' : 'Explore categories',
                primaryAction: () => { setSelectedCategory('all'); setSearchQuery(''); },
                showTrending: false,
                showFallback: marketplaceHasLiveAds !== false && fallbackAds.length > 0,
              },
            };
            const c = COPY[mode];

            return (
              <div className="empty-state" data-testid="empty-search-state" data-mode={mode}>
                <div className="empty-icon" aria-hidden="true">{c.icon}</div>
                <h3 data-testid="home-empty-title">{c.title}</h3>
                <p data-testid="home-empty-text">{c.text}</p>
                <div className="empty-actions">
                  <button type="button" className="btn-reset" onClick={c.primaryAction} data-testid="home-empty-primary-btn">
                    {c.primaryLabel}
                  </button>
                </div>

                {c.showTrending && (
                  <TrendingSearchesChips
                    onSelect={(q) => setSearchQuery(q)}
                    limit={8}
                  />
                )}

                {c.showTrending && searchSuggestions.length > 0 && (
                  <div className="empty-suggestions" data-testid="search-suggestions">
                    <h4>{t('home.trySearchingFor')}</h4>
                    <div className="suggestion-chips">
                      {searchSuggestions.map((s, i) => (
                        <button key={i} className="suggestion-chip" onClick={() => setSearchQuery(s)}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {c.showFallback && (
                  <div className="empty-fallback" data-testid="fallback-listings">
                    <h4>{t('home.youMightAlsoLike')}</h4>
                    <ProductGrid dir={isArabic ? 'rtl' : 'ltr'}>
                      {dedupeAdsById(fallbackAds).slice(0, 4).map((product) => (
                        <ProductCard
                          key={`fb-${product.ad_id}`}
                          product={product}
                          imageUrl={getTrustedImage(product)}
                          title={getAdTitle(product, lang).text || product.title}
                          price={formatMoney(product.price, { locale: isArabic ? 'ar-SD' : 'en', currency: product.currency || 'SDG' })}
                          location={getLocalizedLocationLabel(product, isArabic ? 'ar' : 'en')}
                          isFavorite={isFavorite(product.ad_id)}
                          onOpen={() => navigate(`/ad/${product.ad_id}`)}
                          onToggleFavorite={handleFavoriteClick}
                          onImageError={handleImageError}
                          onImageLoad={handleImageLoad}
                          testId={`fallback-${product.ad_id}`}
                        >
                          <BoostTimerBadge
                            isBoosted={product.is_boosted}
                            boostedUntil={product.boosted_until}
                            status={product.status}
                            visibility={product.visibility}
                            availability={product.availability_status}
                            lang={lang}
                          />
                        </ProductCard>
                      ))}
                    </ProductGrid>
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <>
            <ProductGrid dir={isArabic ? 'rtl' : 'ltr'} className={lowBandwidth ? 'low-bandwidth' : ''}>
              {(displayFeed)
                .map((product) => (
                <ProductCard
                  key={product.ad_id}
                  product={product}
                  imageUrl={getTrustedImage(product)}
                  title={getAdTitle(product, lang).text || product.title}
                  price={formatMoney(product.price, { locale: isArabic ? 'ar-SD' : 'en', currency: product.currency || 'SDG' })}
                  location={cleanLocationLabel(product)}
                  isFavorite={isFavorite(product.ad_id)}
                  onOpen={() => navigate(`/ad/${product.ad_id}`)}
                  onToggleFavorite={handleFavoriteClick}
                  onImageError={handleImageError}
                  onImageLoad={handleImageLoad}
                  testId={`product-${product.ad_id}`}
                >
                  <BoostTimerBadge
                    isBoosted={product.is_boosted}
                    boostedUntil={product.boosted_until}
                    status={product.status}
                    visibility={product.visibility}
                    availability={product.availability_status}
                    lang={lang}
                  />
                  {discoveryMode === 'for_you' && product._fyReason && (
                    <span className="fy-reason">{isRTL ? product._fyReason.ar : product._fyReason.en}</span>
                  )}
                </ProductCard>
              ))}
            </ProductGrid>
            
            {/* Load More Button - Hidden during image search (no pagination for image results) */}
            {hasMore && !isImageSearchActive && (
              <div className="load-more">
                <button 
                  className="btn-load-more"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 
                    (t('crophome.loading')) : 
                    (t('crophome.loadMore'))}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Recently Viewed — per-user, hidden when empty, has clear button. */}
      <RecentlyViewedCarousel />

      {/* Premium Sort Sheet */}
      <PremiumSortSheet
        open={sortOpen}
        onClose={() => setSortOpen(false)}
        value={sortBy}
        onChange={setSortBy}
        onApply={(key) => {
          setSortBy(key);
          setHomeFilters(prev => ({ ...prev, sort: key }));
          setPage(1);
        }}
        onReset={() => {
          setSortBy('newest');
          setHomeFilters(prev => ({ ...prev, sort: 'newest' }));
          setPage(1);
        }}
        isRTL={isRTL}
        variant="glass"
      />
    </div>
  );
}

export default Home;
